import { Box, ButtonBase, Icon, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React from 'react';
import C from '@shared/constants/general';
import { getImage, ImageSize } from '@shared/utils/imageFormat';

const useStyles = (theme) => ({
  image: {
    width: '100% !important',
    borderRadius: '5px !important',
    overflow: 'hidden !important',
    position: 'relative !important',
    paddingBottom: '40% !important',
    backgroundSize: 'cover !important',
    backgroundPosition: 'center !important',
    height: '0px !important',
    '&:hover, &$focusVisible': {
      zIndex: '1 !important',
      '& $imageBackdrop': {
        opacity: '0.8 !important',
      },
    },
  },
  focusVisible: {},
  imageButton: {
    position: 'absolute !important',
    left: '0px !important',
    right: '0px !important',
    top: '0px !important',
    bottom: '0px !important',
    display: 'flex !important',
    alignItems: 'flex-start !important',
    justifyContent: 'flex-end !important',
    flexDirection: 'column !important',
    lineHeight: '1.2rem !important',
    textAlign: 'left !important',
    color: `${theme.palette.common.white} !important`,
  },
  imageLogo: {
    position: 'absolute !important',
    left: '5px !important',
    top: '5px !important',
    width: '18% !important',
    paddingBottom: '18% !important',
    borderRadius: '5px !important',
    backgroundColor: '#000000 !important',
    backgroundSize: 'cover !important',
    backgroundPosition: 'center !important',
  },
  imageSrc: {
    position: 'absolute !important',
    left: '0px !important',
    right: '0px !important',
    top: '0px !important',
    bottom: '0px !important',
    backgroundSize: 'cover !important',
    backgroundPosition: 'center 40% !important',
  },
  imageContainSrc: {
    position: 'absolute !important',
    left: '0px !important',
    right: '0px !important',
    top: '0px !important',
    bottom: '0px !important',
    backgroundSize: 'contain !important',
    backgroundPosition: 'center !important',
    backgroundRepeat: 'no-repeat !important',
  },
  imageBackdrop: {
    position: 'absolute !important',
    left: '0px !important',
    right: '0px !important',
    top: '0px !important',
    bottom: '0px !important',
    borderWidth: '1px !important',
    borderStyle: 'solid !important',
    borderColor: `${theme.palette.primary.main} !important`,
    opacity: '0 !important',
    borderRadius: '5px !important',
    transition: `${theme.transitions.create('opacity')} !important`,
  },
  imageTitle: {
    width: '100% !important',
    backgroundImage:
      'linear-gradient(to bottom, rgba(0,0,0,0), rgba(0,0,0,0.4)) !important',
    fontSize: '1.1rem !important',
    lineHeight: '1.2rem !important',
    position: 'relative !important',
    padding: '7px !important',
    textAlign: 'center !important',
  },
  hidden: {
    opacity: '0.5 !important',
  },
  visitorCount: {
    color: '#fff !important',
    fontSize: '14px !important',
    fontWeight: '600 !important',
    position: 'absolute !important',
    top: '5px !important',
    right: '5px !important',
  },
  visitorIcon: {
    verticalAlign: 'bottom !important',
    fontSize: '20px !important',
    height: '19px !important',
  },
  isLive: {
    fontSize: '14px !important',
    fontWeight: '600 !important',
    position: 'absolute !important',
    top: '5px !important',
    left: '5px !important',
  },
  isLiveIcon: {
    fontSize: '1rem !important',
    verticalAlign: 'baseline !important',
    lineHeight: '1.3rem !important',
  },
});
const EntityListItem = (props) => {
  const theme = useTheme();
  const classes = useStyles(theme);

  // Check if we're in an iframe (where Router is usually not available)
  const isInIframe =
    typeof window !== 'undefined' && window.self !== window.top;

  // Always call useNavigate() unconditionally (React hook requirement)
  // Use navigate prop if provided, otherwise use hook result
  // Note: useNavigate() will throw if not in Router context - this is expected
  // and the component should be used within Router or with navigate prop provided

  const navigate = props.navigate;

  const logo =
    props.entity.picture &&
    getImage(props.entity.picture, ImageSize.Small, props.entity.conferenceId);

  const goto = (url) => {
    // Use navigate if available and not in iframe, otherwise use window.location
    if (navigate && typeof navigate === 'function' && !isInIframe) {
      navigate(url);
    } else {
      window.location.href = url;
    }
  };

  return (
    <ButtonBase
      focusRipple
      href={navigate ? null : props.url}
      onClick={
        navigate || props.url
          ? () => goto(props.url || `/live/item/${props.entity.id}`)
          : null
      }
      sx={{
        ...classes.image,
        ...(props.entity.entityVisitors?.hidden && classes.hidden),
        '&.Mui-focusVisible': {
          ...classes.focusVisible,
        },
      }}
      style={{
        backgroundColor: props.entity.bg_color || '#000000',
        ...(props.entity.bg_picture
          ? {
              backgroundImage: `url(${getImage(
                props.entity.bg_picture,
                ImageSize.Medium,
                props.entity.conferenceId
              )})`,
            }
          : null),
      }}
    >
      <Box component="span" sx={classes.imageBackdrop} />
      {logo && (
        <Box
          component="span"
          sx={classes.imageLogo}
          style={{
            backgroundImage: `url(${logo})`,
            ...(!props.entity.bg_picture
              ? {
                  width: '35%',
                  paddingBottom: '35%',
                }
              : {}),
          }}
        />
      )}
      {props.entity.livestate !== C.ENTITY.LIVE_STATE.OFF && (
        <Typography
          component="div"
          variant="subtitle1"
          color="error"
          sx={classes.isLive}
          style={logo ? { left: '22%' } : {}}
        >
          <Icon sx={classes.isLiveIcon}>fiber_manual_record</Icon>
          LIVE
        </Typography>
      )}
      {Object.prototype.hasOwnProperty.call(props.entity, 'visitorCount') &&
        props.entity.visitorCount > 0 && (
          <Box sx={classes.visitorCount}>
            {props.entity.visitorCount}{' '}
            <Icon sx={classes.visitorIcon}>people</Icon>
          </Box>
        )}
      {props.entity.title && (
        <Box component="span" sx={classes.imageButton}>
          <Typography
            component="span"
            variant="subtitle1"
            color="inherit"
            sx={classes.imageTitle}
          >
            {props.entity.title}
          </Typography>
        </Box>
      )}
    </ButtonBase>
  );
};

export default EntityListItem;
